import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Activity, AlertTriangle, Bike, Boxes, PackageCheck } from 'lucide-react';
import SearchHeader from './SearchHeader';
import ResultsPanel from './ResultsPanel';

const API_URL = process.env.REACT_APP_API_URL;

function StatCard({ icon: Icon, label, value, tone = 'slate' }) {
  const tones = {
    slate: 'bg-white border-slate-200 text-slate-700',
    red: 'bg-red-50 border-red-100 text-red-700',
    emerald: 'bg-emerald-50 border-emerald-100 text-emerald-700',
    indigo: 'bg-indigo-50 border-indigo-100 text-indigo-700',
  };

  return (
    <div className={`rounded-lg border p-3 ${tones[tone] || tones.slate}`}>
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4" />
        <span className="text-xs font-bold uppercase tracking-wide">{label}</span>
      </div>
      <div className="mt-2 text-2xl font-extrabold">{value}</div>
    </div>
  );
}

function SearchGlobalView({ setActiveComponent, onBackClick }) {
  const [query, setQuery] = useState('');
  const [activeType, setActiveType] = useState('all');
  const [results, setResults] = useState([]);
  const [groups, setGroups] = useState({});
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const runSearch = useCallback(async (searchQuery = query, type = activeType) => {
    const trimmed = searchQuery.trim();
    setError('');

    if (!trimmed) {
      setResults([]);
      setGroups({});
      setCounts({});
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/global-search`, {
        params: {
          query: trimmed,
          type,
          limit: 8,
        },
      });

      setResults(response.data.results || []);
      setGroups(response.data.groups || {});
      setCounts(response.data.counts || {});
    } catch (err) {
      console.error('Error fetching global search results:', err);
      setResults([]);
      setGroups({});
      setCounts({});
      setError('No se pudo completar la busqueda global. Revisa el servidor e intenta otra vez.');
    } finally {
      setLoading(false);
    }
  }, [activeType, query]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (query.trim().length >= 2) {
        runSearch(query, activeType);
      } else if (!query.trim()) {
        setResults([]);
        setGroups({});
        setCounts({});
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [query, activeType, runSearch]);

  function handleSubmit(event) {
    event.preventDefault();
    runSearch(query, activeType);
  }

  function handleTypeChange(type) {
    setActiveType(type);
  }

  function handleClear() {
    setQuery('');
    setResults([]);
    setGroups({});
    setCounts({});
    setError('');
  }

  function handleBack() {
    if (setActiveComponent) {
      setActiveComponent('OptionsAdmin');
      return;
    }

    if (onBackClick) {
      onBackClick();
    }
  }

  const overview = useMemo(() => {
    const activeResults = activeType === 'all' ? Object.values(groups).flat() : results;
    const unique = new Map(activeResults.map((item) => [`${item.type}-${item.id}`, item]));
    const all = Array.from(unique.values());

    return {
      total: all.length,
      problems: all.filter((item) => item.flags?.length).length,
      provincia: counts.province_delivery || 0,
      express: counts.express_delivery || 0,
    };
  }, [activeType, counts, groups, results]);

  return (
    <div className="min-h-screen bg-slate-50">
      <SearchHeader
        query={query}
        onQueryChange={setQuery}
        activeType={activeType}
        onTypeChange={handleTypeChange}
        onSubmit={handleSubmit}
        onClear={handleClear}
        onBackClick={handleBack}
        loading={loading}
        counts={{ ...counts, all: results.length }}
      />

      <main className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard icon={Activity} label="Resultados" value={overview.total} />
          <StatCard icon={AlertTriangle} label="Alertas" value={overview.problems} tone="red" />
          <StatCard icon={Boxes} label="Provincia" value={overview.provincia} tone="indigo" />
          <StatCard icon={Bike} label="Express" value={overview.express} tone="emerald" />
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-2 text-xs text-slate-500">
          <PackageCheck className="h-4 w-4" />
          <span>Prioridad: ID exacto, telefonos, tracking y coincidencias directas aparecen primero.</span>
        </div>

        <ResultsPanel
          query={query}
          loading={loading}
          error={error}
          groups={groups}
          results={results}
          activeType={activeType}
          counts={counts}
        />
      </main>
    </div>
  );
}

export default SearchGlobalView;
